import React from 'react';
import StoreIcon from '@mui/icons-material/Store';
import { IconButton, Tooltip, Dialog, DialogContent } from '@mui/material';
import CompanySelectBox from './CompanySelectBox';
import {
  ROLE_NUVE_MASTER_ADMIN,
  ROLE_TECH_ADMIN,
} from '../../../settings/ManageUsers/constants';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField, InputAdornment } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';

function inferGroup(company) {
  if (company.group) return company.group;
  if (company.compType) return company.compType;
  const plan = (company.planName || '').toLowerCase();
  return plan.includes('alpha') ? 'alpha' : 'global';
}

function getLabel(entry) {
  return entry.companyCode
    ? `${entry.name} (${entry.companyCode})`
    : entry.name || '';
}

function getInitials(name) {
  return (name || '')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const C = {
  navy: '#0f1847',
  alpha: '#6d28d9',
  alphaBg: '#f5f3ff',
  alphaBorder: '#ede9fe',
  global: '#0369a1',
  globalBg: '#f0f9ff',
  globalBorder: '#e0f2fe',
  ndsBg: '#f8fafc',
  ndsBorder: '#e2e8f0',
  gold: '#D4AF37',
  selected: '#059669',
  selectedBg: '#ecfdf5',
  border: '#edf0f5',
  muted: '#8a9cc5',
  text: '#1e293b',
};

class CompanyPicker extends React.Component {
  state = {
    dialogOpen: false,
  };

  handleToggle = () => {
    this.setState((prev) => ({ dialogOpen: !prev.dialogOpen }));
  };

  handleClose = () => {
    this.setState({ dialogOpen: false });
  };

  handleCompanyChange = (value) => {
    const originalAuthorities = this.props.userInfo.authority || [];
    const companyAuthorities = (value.authorities || []).map(
      (item) => item.authority || item
    );
    const originalRoutes = this.props.userInfo.routes || [];
    const companyRoutes = value.routes || [];

    let authorityArray;
    let routesArray;

    if (
      originalAuthorities.includes(ROLE_TECH_ADMIN) ||
      originalAuthorities.includes(ROLE_NUVE_MASTER_ADMIN)
    ) {
      authorityArray = originalAuthorities;
      routesArray = originalRoutes;
    } else {
      authorityArray = companyAuthorities.length
        ? companyAuthorities
        : originalAuthorities;
      routesArray = companyRoutes.length ? companyRoutes : originalRoutes;
    }

    const updatedUserInfo = {
      ...this.props.userInfo,
      routes: routesArray,
      authority: authorityArray,
      selectedLocationId: value.companyId,
      selectedLocationName:
        value.name || this.props.userInfo.selectedLocationName,
      planName: value.planName,
    };

    // Persist the selected companyId so AuthUtil can stamp it on every
    // subsequent request as the X-Selected-Company header. Without this,
    // the backend LockGateInterceptor and AccountService.getUserInfo
    // would keep evaluating the user's home company, letting a user
    // switch to a locked linked company and operate it.
    if (value.companyId) {
      localStorage.setItem('selectedCompanyId', value.companyId);
    }

    this.props.updateUserInfo(updatedUserInfo);
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  renderNDSCard(nds, isSelected) {
    return (
      <div
        onClick={() => !isSelected && this.handleCompanyChange(nds)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          borderRadius: 10,
          cursor: isSelected ? 'default' : 'pointer',
          background: isSelected ? C.selectedBg : C.ndsBg,
          border: `1.5px solid ${isSelected ? C.selected : C.ndsBorder}`,
          transition: 'all .15s',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            flexShrink: 0,
            background: `linear-gradient(135deg, ${C.navy}, #1a2666)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.gold,
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          NDS
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>
            NuVe System
          </div>
          <div style={{ fontSize: 11, color: C.muted }}>
            {(this.props.userInfo.authority || []).includes(ROLE_TECH_ADMIN) &&
            !(this.props.userInfo.authority || []).includes(
              ROLE_NUVE_MASTER_ADMIN
            )
              ? 'Tech Admin'
              : 'Owner'}
          </div>
        </div>
        {isSelected && (
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 4,
              background: C.selected,
              color: '#fff',
            }}
          >
            ACTIVE
          </div>
        )}
      </div>
    );
  }

  renderGroupSection(
    companies,
    selectedId,
    accentColor,
    bgColor,
    borderColor,
    title,
    placeholder
  ) {
    const selected = companies.find((c) => c.companyId === selectedId) || null;

    const filterFn = (opts, state) =>
      opts.filter((o) => {
        const input = state.inputValue.toLowerCase();
        return (
          o.name.toLowerCase().includes(input) ||
          (o.companyCode && o.companyCode.toLowerCase().includes(input))
        );
      });

    return (
      <div
        style={{
          background: bgColor,
          borderRadius: 12,
          padding: 14,
          border: `1px solid ${borderColor}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: accentColor,
              }}
            />
            <span style={{ fontSize: 13, fontWeight: 700, color: accentColor }}>
              {title}
            </span>
          </div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: accentColor,
              background: `${accentColor}15`,
              padding: '2px 8px',
              borderRadius: 10,
            }}
          >
            {companies.length}
          </span>
        </div>

        <Autocomplete
          options={companies}
          getOptionLabel={getLabel}
          autoHighlight
          disableClearable={!!selected}
          onChange={(e, value) => value && this.handleCompanyChange(value)}
          blurOnSelect
          filterOptions={filterFn}
          value={selected}
          renderOption={(option) => {
            const isCurrent = option.companyId === selectedId;
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '4px 0',
                }}
              >
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    flexShrink: 0,
                    background: isCurrent ? C.selected : `${accentColor}18`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isCurrent ? '#fff' : accentColor,
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                >
                  {getInitials(option.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: isCurrent ? C.selected : C.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {option.name}
                  </div>
                  {option.companyCode && (
                    <div style={{ fontSize: 11, color: C.muted }}>
                      {option.companyCode}
                    </div>
                  )}
                </div>
                {isCurrent && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={C.selected}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <SearchIcon style={{ fontSize: 18, color: C.muted }} />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
                style: { fontSize: 13, background: '#fff', borderRadius: 8 },
              }}
            />
          )}
        />
      </div>
    );
  }

  renderGroupedPicker() {
    const allCompanies = this.props.availableCompanies || [];
    const selectedId = this.props.userInfo.selectedLocationId;

    const alphaCompanies = allCompanies.filter(
      (c) => c.name !== 'NuVe System' && inferGroup(c) === 'alpha'
    );
    const globalCompanies = allCompanies.filter(
      (c) => c.name !== 'NuVe System' && inferGroup(c) === 'global'
    );
    const nds = allCompanies.find((c) => c.name === 'NuVe System');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {nds && this.renderNDSCard(nds, nds.companyId === selectedId)}

        {alphaCompanies.length > 0 &&
          this.renderGroupSection(
            alphaCompanies,
            selectedId,
            C.alpha,
            C.alphaBg,
            C.alphaBorder,
            'Alpha',
            'Search alpha locations...'
          )}

        {globalCompanies.length > 0 &&
          this.renderGroupSection(
            globalCompanies,
            selectedId,
            C.global,
            C.globalBg,
            C.globalBorder,
            'Global',
            'Search global locations...'
          )}
      </div>
    );
  }

  render() {
    const userInfo = this.props.userInfo || {};
    const authorities = userInfo.authority || [];
    // Owners carry every role including ROLE_TECH_ADMIN, so only
    // treat non-owners as real tech admins here.
    const isTechAdmin =
      authorities.includes(ROLE_TECH_ADMIN) &&
      !authorities.includes(ROLE_NUVE_MASTER_ADMIN);
    const companyIds = isTechAdmin
      ? userInfo.selectedLocations || []
      : userInfo.companyIds && userInfo.companyIds.length > 0
      ? userInfo.companyIds
      : userInfo.selectedLocations || [];
    const { dialogOpen } = this.state;
    if (companyIds.length === 0) return null;

    const showGrouped = (this.props.availableCompanies || []).length > 0;

    return (
      <>
        <div>
          <Tooltip title="Select Company" arrow>
            <IconButton size="large" onClick={this.handleToggle}>
              <StoreIcon
                style={{ fontSize: '30px', color: 'rgb(120, 150, 255)' }}
              />
            </IconButton>
          </Tooltip>
        </div>

        <Dialog
          open={dialogOpen}
          onClose={this.handleClose}
          maxWidth="xs"
          fullWidth
          disableScrollLock={false}
          scroll="paper"
          style={{ alignItems: 'flex-start' }}
          PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
        >
          {showGrouped ? (
            <DialogContent style={{ padding: 16 }}>
              {this.renderGroupedPicker()}
            </DialogContent>
          ) : (
            <>
              <div
                style={{
                  padding: '16px 16px 0',
                  fontSize: 16,
                  fontWeight: 700,
                  color: C.navy,
                }}
              >
                Select Company
              </div>
              <DialogContent>
                <CompanySelectBox
                  userInfo={this.props.userInfo}
                  selectedLocations={companyIds}
                  updateCallback={this.handleCompanyChange}
                />
              </DialogContent>
            </>
          )}
        </Dialog>
      </>
    );
  }
}

export default CompanyPicker;
